import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, ProgressBar } from 'react-bootstrap';
import { FaCode, FaLaptopCode, FaDatabase, FaMobileAlt, FaArrowRight, FaArrowLeft } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import './Assessment.css';

const Assessment = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    education: '',
    experience: '',
    interests: []
  });

  const interests = [
    { id: 'web', label: 'Web Development', icon: <FaCode /> },
    { id: 'mobile', label: 'Mobile Development', icon: <FaMobileAlt /> },
    { id: 'data', label: 'Data Science', icon: <FaDatabase /> },
    { id: 'fullstack', label: 'Full Stack Development', icon: <FaLaptopCode /> }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleInterestToggle = (interestId) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interestId)
        ? prev.interests.filter(id => id !== interestId)
        : [...prev.interests, interestId]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset, velocity) => {
    return Math.abs(offset) * velocity;
  };

  const [[page, direction], setPage] = useState([0, 0]);

  const paginate = (newDirection) => {
    setPage([page + newDirection, newDirection]);
  };

  return (
    <div className="assessment-page">
      <motion.div 
        className="assessment-hero"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <Container>
          <Row className="justify-content-center text-center">
            <Col md={8}>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
              >
                Skill Assessment
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
              >
                Let's find the perfect learning path for you
              </motion.p>
            </Col>
          </Row>
        </Container>
      </motion.div>

      <Container className="assessment-container">
        <Row className="justify-content-center">
          <Col md={8}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Card className="assessment-card">
                <Card.Body>
                  <div className="assessment-progress mb-4">
                    <ProgressBar 
                      now={(currentStep / 3) * 100} 
                      className="custom-progress"
                    />
                    <div className="progress-steps">
                      <motion.span 
                        className={currentStep >= 1 ? 'active' : ''}
                        whileHover={{ scale: 1.1 }}
                      >
                        Basic Info
                      </motion.span>
                      <motion.span 
                        className={currentStep >= 2 ? 'active' : ''}
                        whileHover={{ scale: 1.1 }}
                      >
                        Skills
                      </motion.span>
                      <motion.span 
                        className={currentStep >= 3 ? 'active' : ''}
                        whileHover={{ scale: 1.1 }}
                      >
                        Results
                      </motion.span>
                    </div>
                  </div>

                  <AnimatePresence initial={false} custom={direction}>
                    <motion.div
                      key={page}
                      custom={direction}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{
                        x: { type: "spring", stiffness: 300, damping: 30 },
                        opacity: { duration: 0.2 }
                      }}
                      drag="x"
                      dragConstraints={{ left: 0, right: 0 }}
                      dragElastic={1}
                      onDragEnd={(e, { offset, velocity }) => {
                        const swipe = swipePower(offset.x, velocity.x);

                        if (swipe < -swipeConfidenceThreshold) {
                          paginate(1);
                        } else if (swipe > swipeConfidenceThreshold) {
                          paginate(-1);
                        }
                      }}
                    >
                      <Form onSubmit={handleSubmit}>
                        {currentStep === 1 && (
                          <motion.div 
                            className="assessment-step"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                          >
                            <h3>Tell us about yourself</h3>
                            <Form.Group className="mb-3">
                              <Form.Label>Full Name</Form.Label>
                              <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                                className="animated-input"
                              />
                            </Form.Group>

                            <Form.Group className="mb-3">
                              <Form.Label>Email</Form.Label>
                              <Form.Control
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                className="animated-input"
                              />
                            </Form.Group>

                            <Form.Group className="mb-3">
                              <Form.Label>Education Level</Form.Label>
                              <Form.Select
                                name="education"
                                value={formData.education}
                                onChange={handleInputChange}
                                required
                                className="animated-input"
                              >
                                <option value="">Select your education level</option>
                                <option value="high-school">High School</option>
                                <option value="bachelors">Bachelor's Degree</option>
                                <option value="masters">Master's Degree</option>
                                <option value="phd">PhD</option>
                              </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                              <Form.Label>Years of Experience</Form.Label>
                              <Form.Select
                                name="experience"
                                value={formData.experience}
                                onChange={handleInputChange}
                                required
                                className="animated-input"
                              >
                                <option value="">Select your experience</option>
                                <option value="0">No experience</option>
                                <option value="1-2">1-2 years</option>
                                <option value="3-5">3-5 years</option>
                                <option value="5+">5+ years</option>
                              </Form.Select>
                            </Form.Group>

                            <motion.div
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <Button
                                variant="primary"
                                className="w-100 next-button"
                                onClick={() => setCurrentStep(2)}
                              >
                                Next Step <FaArrowRight className="ms-2" />
                              </Button>
                            </motion.div>
                          </motion.div>
                        )}

                        {currentStep === 2 && (
                          <motion.div 
                            className="assessment-step"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                          >
                            <h3>What are you interested in?</h3>
                            <p className="text-muted mb-4">Select all that apply</p>

                            <div className="interests-grid">
                              {interests.map(interest => (
                                <motion.div
                                  key={interest.id}
                                  className={`interest-card ${
                                    formData.interests.includes(interest.id) ? 'selected' : ''
                                  }`}
                                  onClick={() => handleInterestToggle(interest.id)}
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                >
                                  <motion.div 
                                    className="interest-icon"
                                    whileHover={{ rotate: 360 }}
                                    transition={{ duration: 0.5 }}
                                  >
                                    {interest.icon}
                                  </motion.div>
                                  <h4>{interest.label}</h4>
                                </motion.div>
                              ))}
                            </div>

                            <div className="d-flex gap-3 mt-4">
                              <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="flex-grow-1"
                              >
                                <Button
                                  variant="outline-primary"
                                  className="w-100"
                                  onClick={() => setCurrentStep(1)}
                                >
                                  <FaArrowLeft className="me-2" /> Back
                                </Button>
                              </motion.div>
                              <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="flex-grow-1"
                              >
                                <Button
                                  variant="primary"
                                  className="w-100"
                                  onClick={() => setCurrentStep(3)}
                                >
                                  View Results <FaArrowRight className="ms-2" />
                                </Button>
                              </motion.div>
                            </div>
                          </motion.div>
                        )}

                        {currentStep === 3 && (
                          <motion.div 
                            className="assessment-step"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                          >
                            <h3>Your Personalized Learning Path</h3>
                            <div className="results-content">
                              <motion.div 
                                className="result-card"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                              >
                                <h4>Recommended Learning Track</h4>
                                <p>Based on your interests and experience, we recommend:</p>
                                <motion.div 
                                  className="recommended-track"
                                  whileHover={{ scale: 1.02 }}
                                >
                                  <h5>Full Stack Web Development</h5>
                                  <p>Master both frontend and backend development</p>
                                </motion.div>
                              </motion.div>

                              <motion.div 
                                className="result-card"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.4 }}
                              >
                                <h4>Suggested Projects</h4>
                                <motion.ul className="project-list">
                                  <motion.li
                                    whileHover={{ x: 10 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                  >
                                    E-commerce Website
                                  </motion.li>
                                  <motion.li
                                    whileHover={{ x: 10 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                  >
                                    Task Management System
                                  </motion.li>
                                  <motion.li
                                    whileHover={{ x: 10 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                  >
                                    Social Media Dashboard
                                  </motion.li>
                                </motion.ul>
                              </motion.div>

                              <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                              >
                                <Button
                                  variant="primary"
                                  className="w-100 mt-4 start-learning-btn"
                                  onClick={() => window.location.href = '/register'}
                                >
                                  Start Learning Now
                                </Button>
                              </motion.div>
                            </div>
                          </motion.div>
                        )}
                      </Form>
                    </motion.div>
                  </AnimatePresence>
                </Card.Body>
              </Card>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Assessment; 